export type GpsPosition = {
  lat: number;
  lng: number;
};

export enum Events {
  // host to map
  SET_THEME = 'set-theme',
  SET_CENTER = 'set-center',
  SET_RANGE = 'set-range',
  SET_MARKERS = 'set-markers',
  SET_CAN_MOVE = 'set-can-move',
  SET_ANIMATE = 'set-animate',
  FIT_TO_VIEW = 'fit-to-view',
  // map to host
  ON_INITIALIZED = 'on-initialized',
  ON_MOVE_END = 'on-move-end',
  ON_MARKER_CLICK = 'on-marker-click'
}

export enum MarkerType {
  HOME = 'home',
  NEAR = 'near',
  ADD = 'add',
  CHECK = 'check',
  PIN = 'pin'
}

type TEvent = (typeof Events)[keyof typeof Events];

export type GeoArea = {
  id: string;
  position: GpsPosition;
};

export type GeoAreaMarker = GeoArea & {
  name?: string;
  type: MarkerType;
  size: number;
  color?: string;
};

type TEventMessageData<E extends TEvent, P = unknown> = {
  event: E;
  payload: P;
};

export type SetThemePayload = {
  markerColor: string;
  rangeColor: string;
};

export type SetCenterPayload = {
  position: GpsPosition;
};

export type SetMarkersPayload = {
  markers: GeoAreaMarker[];
};

export type SetRangePayload = {
  meters: number;
};

export type SetCanMovePayload = {
  canMove: boolean;
};

export type SetAnimatePayload = {
  animate: boolean;
};

export type FitToViewPayload = {
  center: boolean;
  markers: boolean;
  range: boolean;
  zoom?: number;
};

export type OnMoveEndPayload = {
  position: GpsPosition;
};

export type OnMarkerClickPayload = {
  marker: GeoAreaMarker;
};

export type SetThemeMessageData = TEventMessageData<
  Events.SET_THEME,
  SetThemePayload
>;

export type SetCenterMessageData = TEventMessageData<
  Events.SET_CENTER,
  SetCenterPayload
>;

export type SetRangeMessageData = TEventMessageData<
  Events.SET_RANGE,
  SetRangePayload
>;

export type SetMarkersMessageData = TEventMessageData<
  Events.SET_MARKERS,
  SetMarkersPayload
>;

export type SetCanMoveMessageData = TEventMessageData<
  Events.SET_CAN_MOVE,
  SetCanMovePayload
>;

export type SetAnimateMessageData = TEventMessageData<
  Events.SET_ANIMATE,
  SetAnimatePayload
>;

export type FitToViewMessageData = TEventMessageData<
  Events.FIT_TO_VIEW,
  FitToViewPayload
>;

export type OnInitializedMessageData = TEventMessageData<Events.ON_INITIALIZED>;

export type OnMoveEndMessageData = TEventMessageData<
  Events.ON_MOVE_END,
  OnMoveEndPayload
>;

export type OnMarkerClickMessageData = TEventMessageData<
  Events.ON_MARKER_CLICK,
  OnMarkerClickPayload
>;

export type MapMessageData =
  | SetThemeMessageData
  | SetCenterMessageData
  | SetRangeMessageData
  | SetMarkersMessageData
  | SetCanMoveMessageData
  | SetAnimateMessageData
  | FitToViewMessageData
  | OnInitializedMessageData
  | OnMoveEndMessageData
  | OnMarkerClickMessageData;

export type MapMessageEvent = MessageEvent<MapMessageData>;

export const isMapMessageData = (data: unknown): data is MapMessageData =>
  data !== null &&
  typeof data === 'object' &&
  typeof (data as { event?: unknown }).event === 'string';
